import axios, { AxiosError, AxiosResponse } from 'axios';
import { getLogger } from '../diagnostics/logger';
import queryString from 'query-string';
import { getSimManagementErrorFromAxiosError } from '.';
import { AccountDetail, AccountLoadParameters } from '../models/account';
import { QueryResult } from '../models/queryResult';
import { tryParseInt } from './utilities';
import { IDashboardData } from '../components/graphicalDashboard/GraphicalDashboard';

import config from '../config';
const log = getLogger('simManagement.api.accountApi');

interface AccountSearchOptions {
  take: number;
  skip: number;
  searchTerm: string;
  wrapWith: {
    totalCount: boolean;
  };
  excludedAccounts?: number[];
}

interface QueryOptions {
  nameContains?: string;
  id?: number;
  skip: number;
  take: number;
  wrapWith?: string;
  'id!'?: string;
}

const defaultSearchOptions: AccountSearchOptions = {
  searchTerm: '',
  skip: 0,
  take: 50,
  wrapWith: {
    totalCount: false
  },
};

export function getById(value: AccountLoadParameters): Promise<AccountDetail> {
  const url = config.API_BASE_URL + `api/company/${value.companyId}/account/${value.accountId}`;

  return new Promise<AccountDetail>((resolve, reject) => {
    axios
      .get(url)
      .then((response: any) => {
        const result = response.data as AccountDetail;
        resolve(result);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to load account ${value.accountId}.`, simManagementError);
        reject(simManagementError);
      });
  });
}

export function get(companyId: number, options: Partial<AccountSearchOptions>): Promise<QueryResult<AccountDetail>> {
  const resolvedOptions = Object.assign(defaultSearchOptions, options);
  const queryOptions: QueryOptions = {
    skip: resolvedOptions.skip,
    take: resolvedOptions.take,
  };

  if (resolvedOptions.searchTerm) {
    const accountId = tryParseInt(resolvedOptions.searchTerm, NaN);
    if (isNaN(accountId)) {
      queryOptions.nameContains = resolvedOptions.searchTerm;
    } else {
      queryOptions.id = accountId;
    }
  }

  const wrapWithItems = ['count'];

  if (resolvedOptions.wrapWith.totalCount) {
    wrapWithItems.push('total-count');
  }

  if (wrapWithItems.length > 0) {
    queryOptions.wrapWith = wrapWithItems.join(',');
  }

  const excludedAccounts = options.excludedAccounts;

  if (excludedAccounts) {
    queryOptions['id!'] = excludedAccounts.join(',');
  }

  const queryParams = queryString.stringify(queryOptions);
  const url = config.API_BASE_URL + `api/company/${companyId}/account?${queryParams}`;

  return new Promise<QueryResult<AccountDetail>>((resolve, reject) => {
    axios
      .get(url)
      .then((response: AxiosResponse<QueryResult<AccountDetail>>) => {
        const result = response.data;
        resolve(result);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to get accounts for company ${companyId}...`, simManagementError);
        reject(simManagementError);
      });
  });
}

export function getDashboardDataForAccount(companyId: number, accountId: number) {
  const baseUrl = config.API_BASE_URL + `api/company/${companyId}/account/${accountId}/dashboard`;

  return new Promise<IDashboardData>((resolve, reject) => {
    axios
      .get(baseUrl)
      .then((response: AxiosResponse<IDashboardData>) => {
        resolve(response.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}
